import React, { useState } from 'react';
import { flowRight } from 'lodash';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import {
  ELSCheckBox,
  ELSIcon
} from '@els/els-component-form-field-react';
import {
  PromptOverrideConfigDto,
  OverridePromptType,
  PromptOverrideDto,
  SearchOverrideDto
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import withPageLoader from '../../hocs/with-page-loader/withPageLoader.hoc';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import 'chartjs-plugin-datalabels';
import { BASE_TOAST_CONFIG } from '../../constants/toast.constants';
import { copyContentToClipboard, stripEmptyProps } from '../../utilities/common.utilities';

type AiChatAdminModalPropsOnly = {
  promptOverride: PromptOverrideDto;
  searchOverride: SearchOverrideDto;
  adminOverridePageContext: string;
  handleSave: (
    promptOverride: PromptOverrideDto,
    searchOverride: SearchOverrideDto,
    adminOverridePageContext: string
  ) => void;
  handleCancel: () => void;
}

export type AiChatAdminModalProps = ELSPropsFromToastService & AiChatAdminModalPropsOnly;

export type AiChatAdminModalState = {
  convertInput: string;
  promptOverride: Record<OverridePromptType, PromptOverrideConfigDto>;
  promptOverrideActiveKeys: Record<OverridePromptType, boolean>;
  searchOverrideIsbns: string;
  includeBaseSet: boolean;
  adminOverridePageContext: string;
}

enum ConfigField {
  prompt = 'prompt',
  userChatItemTemplate = 'userChatItemTemplate',
  botChatItemTemplate = 'botChatItemTemplate',
  promptBookChunkItemTemplate = 'promptBookChunkItemTemplate',
  promptVideoItemTemplate = 'promptVideoItemTemplate',
  promptContentChunkItemTemplate = 'promptContentChunkItemTemplate',
  model = 'model',
  temperature = 'temperature',
  reasoning = 'reasoning',
  provider = 'provider',
  virtualKey = 'virtualKey',
  cerebusApiKey = 'cerebusApiKey',
  config = 'config'
}

const MODEL_FIELDS: ConfigField[] = [
  ConfigField.model,
  ConfigField.temperature,
  ConfigField.reasoning,
  ConfigField.provider,
  ConfigField.virtualKey,
  ConfigField.cerebusApiKey,
  ConfigField.config
];

const MODEL_FIELD_PLACEHOLDERS: Partial<Record<ConfigField, string>> = {
  [ConfigField.model]: 'us.anthropic.claude-haiku-4-5-20251001-v1:0',
  [ConfigField.temperature]: '0.0',
  [ConfigField.reasoning]: 'medium',
  [ConfigField.provider]: 'bedrock',
  [ConfigField.virtualKey]: '',
  [ConfigField.cerebusApiKey]: '',
  [ConfigField.config]: ''
};

const promptFields = [
  'BOOK_CONTENT',
  'VIDEO_CONTENT',
  'CHAT_HISTORY',
  'USER_MESSAGE',
  'SEARCH_CONTEXTUALIZATION',
  'DISCIPLINE_TITLE_LIST',
  'DISCIPLINE_TITLE_BULLETS',
  'DISCIPLINE_TITLE_LIST_COMMAS',
  'DISCIPLINE_CERT_LIST',
  'DISCIPLINE_CONCEPT_LIST'
];

const exampleMap: Partial<Record<ConfigField, string>> = {
  [ConfigField.userChatItemTemplate]: '{{MESSAGE}}',
  [ConfigField.botChatItemTemplate]: '{{MESSAGE}}',
  [ConfigField.prompt]: promptFields.map((field) => {
    return `{{${field}}}`;
  }).join(' '),
  [ConfigField.promptBookChunkItemTemplate]: '{{MAP_ID}} {{BOOK_TITLE}} {{BOOK_SECTION}} {{BOOK_CONTENT}}',
  [ConfigField.promptVideoItemTemplate]: '{{MAP_ID}} {{VIDEO_TITLE}} {{VIDEO_CONTENT}}',
  [ConfigField.promptContentChunkItemTemplate]: '{{MAP_ID}} {{CONTENT_CHUNK}}'
};

const responseFormatMap: Record<OverridePromptType, string> = {
  [OverridePromptType.chatbotCapabilities]: 'This has no expected output as it fulfills a placeholder in many prompts -- it is not a prompt itself.',
  [OverridePromptType.relatedContentSummarization]: 'any text',
  [OverridePromptType.relatedContentRateVectorResults]: JSON.stringify([
    {
      id: 1, // number
      rating: 1, // number
      thinking: 'any text'
    }
  ]),
  [OverridePromptType.declineResponse]: 'any text',
  [OverridePromptType.respondWithoutCitations]: 'any text',
  [OverridePromptType.primaryResponse]: 'any text',
  [OverridePromptType.primaryResponseTutorMode]: 'any text',
  [OverridePromptType.classificationIsRelevant]: JSON.stringify({
    userMessageTopics: ['List of keywords and topics related to last user message'],
    isUserMessageRelevant: 'YES/NO'
  }),
  [OverridePromptType.declineResponseNoSearchResults]: 'any text',
  [OverridePromptType.studyContentGenerationResponse]: 'any text',
  [OverridePromptType.postProcess]: JSON.stringify({
    bookChunks: [
      {
        bookChunkId: -1,
        bookChunkWasUsed: true
      },
      {
        bookChunkId: -2,
        bookChunkWasUsed: false
      }
    ],
    videoTranscriptChunks: [
      {
        videoTranscriptChunkId: -3,
        videoChunkInclude: true
      },
      {
        videoTranscriptChunkId: -4,
        videoChunkInclude: false
      }
    ]
  }),
  [OverridePromptType.rankAndFilterSearchResults]: JSON.stringify(['123', '456']),
  [OverridePromptType.classificationStudyContentGeneration]: JSON.stringify({
    thinking: 'summary of last user message intent and your thinking',
    isLastUserMessageAskingToGenerateQuizQuestions: 'YES/NO'
  }),
  [OverridePromptType.contextResolution]: 'any text',
  [OverridePromptType.followUpQuestions]: 'any text',
  [OverridePromptType.flashcardAcknowledgment]: 'any text',
  [OverridePromptType.flashcardGeneration]: JSON.stringify({
    flashcards: [
      {
        front: 'Question or term',
        back: 'Answer or definition',
        reference_indices: [0, 2]
      }
    ]
  }),
  [OverridePromptType.tableContextResolution]: 'any text',
  [OverridePromptType.tableRankAndFilterSearchResults]: JSON.stringify(['123', '456']),
};

const emptyConfig = (): PromptOverrideConfigDto => {
  return {
    prompt: '',
    userChatItemTemplate: '',
    botChatItemTemplate: '',
    promptBookChunkItemTemplate: '',
    promptVideoItemTemplate: '',
    promptContentChunkItemTemplate: '',
    model: '',
    temperature: '',
    reasoning: '',
    provider: '',
    virtualKey: '',
    cerebusApiKey: '',
    config: ''
  };
};

const defaultState: AiChatAdminModalState = {
  convertInput: '',
  promptOverrideActiveKeys: Object.keys(OverridePromptType).reduce((acc, key) => {
    return {
      ...acc,
      [key]: false
    };
  }, {} as Record<OverridePromptType, boolean>),
  promptOverride: Object.keys(OverridePromptType).reduce((acc, key) => {
    return {
      ...acc,
      [key]: emptyConfig()
    };
  }, {} as Record<OverridePromptType, PromptOverrideConfigDto>),
  searchOverrideIsbns: '',
  includeBaseSet: true,
  adminOverridePageContext: ''
};

export const AiChatAdminModalComponent = (props: AiChatAdminModalProps): JSX.Element => {
  const {
    handleSave,
    handleCancel,
    toastService,
  } = props;

  const mergeIncomingOverrides = (
    incoming: PromptOverrideDto
  ): Record<OverridePromptType, PromptOverrideConfigDto> => {
    const merged = { ...defaultState.promptOverride };
    Object.keys(incoming || {}).forEach((key) => {
      const base = emptyConfig();
      const incomingConfig = (incoming[key] || {}) as Partial<PromptOverrideConfigDto>;
      (Object.keys(base) as (keyof PromptOverrideConfigDto)[]).forEach((field) => {
        const value = incomingConfig[field];
        base[field] = value == null ? '' : value;
      });
      merged[key as OverridePromptType] = base;
    });
    return merged;
  };

  const getDefaultOverrideState = (
    promptOverride: PromptOverrideDto,
    searchOverride: SearchOverrideDto,
    adminOverridePageContext: string
  ): Partial<AiChatAdminModalState> => {
    return {
      promptOverrideActiveKeys: {
        ...defaultState.promptOverrideActiveKeys,
        ...Object.keys(promptOverride || {}).reduce((acc, key) => {
          return {
            ...acc,
            [key]: true
          };
        }, {} as Record<OverridePromptType, boolean>)
      },
      promptOverride: mergeIncomingOverrides(promptOverride),
      searchOverrideIsbns: searchOverride?.isbns ? searchOverride.isbns.join(',') : '',
      includeBaseSet: searchOverride?.isUseBaseSet !== undefined ? searchOverride.isUseBaseSet : defaultState.includeBaseSet,
      adminOverridePageContext: adminOverridePageContext || ''
    };
  };

  const [state, setState] = useState<AiChatAdminModalState>({
    ...defaultState,
    ...getDefaultOverrideState(props.promptOverride, props.searchOverride, props.adminOverridePageContext),
  });

  const handleFieldChange = (e, prompt: OverridePromptType, field: ConfigField) => {
    const { value } = e.target;
    setState((prevState) => {
      return {
        ...prevState,
        promptOverride: {
          ...prevState.promptOverride,
          [prompt]: {
            ...prevState.promptOverride[prompt],
            [field]: value
          }
        }
      };
    });
  };

  const scrubIsbn = (isbn: string) => {
    if (!isbn) {
      return '';
    }
    return isbn.replace(/[^0-9,]/g, '').trim();
  };

  const handleIsbnsChange = (e) => {
    const { value } = e.target;
    setState((prevState) => {
      return {
        ...prevState,
        searchOverrideIsbns: scrubIsbn(value)
      };
    });
  };

  const getOverridesForSave = (): PromptOverrideDto => {
    return Object.keys(state.promptOverride).reduce((acc, key) => {
      if (!state.promptOverrideActiveKeys[key]) {
        return acc;
      }

      // Include only fields the admin actually set; blank fields are stripped so
      // we never send '' or null over the wire.
      const config: PromptOverrideConfigDto = stripEmptyProps(state.promptOverride[key]);

      // If the active override has no non-blank fields, drop the prompt key
      // entirely rather than sending an empty {}.
      if (!Object.keys(config).length) {
        return acc;
      }

      return {
        ...acc,
        [key]: config
      };
    }, {});
  };

  const getIsbns = (isbnsString: string): string[] => {
    if (!isbnsString) {
      return [];
    }
    return isbnsString
      .split(',')
      .map((isbn) => {
        return scrubIsbn(isbn);
      })
      .filter((item) => {
        return item !== '';
      });
  };

  const getSearchOverridesForSave = (): SearchOverrideDto => {
    const isbns = getIsbns(state.searchOverrideIsbns);
    if (!isbns.length && state.includeBaseSet) {
      return null;
    }

    // Sending empty array will default back to all user entitlements
    return {
      isbns,
      isUseBaseSet: state.includeBaseSet,
    };
  };

  const handleCopyClick = (content: string) => {
    copyContentToClipboard(content);
    toastService.openToast({
      ...BASE_TOAST_CONFIG,
      component: (
        <div>Successfully copied</div>
      ),
      type: ELSWithToastService.types.POSITIVE
    });
  };

  const extractString = (input, start, stop) => {
    const startIndex = input.indexOf(start) + start.length;
    const stopIndex = input.indexOf(stop);
    if (startIndex === -1 || stopIndex === -1) {
      return null; // start or stop not found in the input
    }
    return input.substring(startIndex, stopIndex);
  };

  const renderModelField = (prompt: OverridePromptType, field: ConfigField) => {
    return (
      <div
        key={`${prompt}_${field}`}
        className="c-naa-ai-chat-admin-override-modal__field"
      >
        <div>{field}</div>
        <div className="c-els-field">
          <input
            type="text"
            name={field}
            className="c-els-field__input"
            value={state.promptOverride[prompt][field] || ''}
            placeholder={MODEL_FIELD_PLACEHOLDERS[field] || ''}
            onChange={(e) => {
              handleFieldChange(e, prompt, field);
            }}
          />
        </div>
      </div>
    );
  };

  const renderPromptField = (prompt: OverridePromptType, field: ConfigField) => {
    return (
      <div
        key={`${prompt}_${field}`}
        className="c-naa-ai-chat-admin-override-modal__field"
      >
        <div>{field}</div>
        <div className="c-naa-ai-chat-admin-override-modal__description">
          <div>Replace fields:</div>
          <div>{exampleMap[field]}</div>
        </div>
        <div className="c-els-field">
          <textarea
            name={field}
            className="c-els-field__input"
            value={state.promptOverride[prompt][field] || ''}
            onChange={(e) => {
              handleFieldChange(e, prompt, field);
            }}
          />
        </div>
      </div>
    );
  };

  const renderConfig = (prompt: OverridePromptType) => {
    const allFields = Object.keys(ConfigField) as ConfigField[];
    const promptFieldList = allFields.filter((field) => {
      return !MODEL_FIELDS.includes(field);
    });
    return (
      <>
        <div className="c-naa-ai-chat-admin-override-modal__field-group">
          <h5 className="c-naa-ai-chat-admin-override-modal__field-group-title">Model override</h5>
          {MODEL_FIELDS.map((field) => {
            return renderModelField(prompt, field);
          })}
        </div>
        {promptFieldList.map((field) => {
          return renderPromptField(prompt, field);
        })}
      </>
    );
  };

  return (
    <div>
      <div className="c-els-modal c-els-modal--full">
        <div className="c-els-modal__window u-els-width-1o1 c-naa-ai-chat-admin-override-modal">
          <div className="c-naa-ai-chat-admin-override-modal__top">
            <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.GUTTERS]}>
              <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                <h2>Admin overrides</h2>
              </FlexItem>
              <FlexItem>
                <button
                  type="button"
                  className="u-els-debuttonize"
                  onClick={handleCancel}
                >
                  <ELSIcon name="close" size="1x" />
                  <span className="u-els-hide-visually">Close</span>
                </button>
              </FlexItem>
            </FlexLayout>
          </div>
          <div className="c-naa-ai-chat-admin-override-modal__main">
            <div>
              <div className="o-els-container">
                <h4>Converter tool</h4>
                <div className="c-els-field">
                  <textarea
                    name="converter"
                    className="c-els-field__input"
                    value={state.convertInput}
                    onChange={(e) => {
                      const { value } = e.target;
                      setState((prevState) => {
                        return {
                          ...prevState,
                          convertInput: value
                        };
                      });
                    }}
                  />
                </div>
              </div>
              <div className="o-els-container">
                <FlexLayout modifiers={[
                  FlexLayoutModifier.LEFT,
                  FlexLayoutModifier.GUTTERS
                ]}>
                  <FlexItem>
                    <button
                      type="button"
                      className="c-els-button c-els-button--small"
                      onClick={() => {
                        setState((prevState) => {
                          let parsed = state.convertInput;
                          try {
                            const str = `{"value": "${state.convertInput}"}`;
                            parsed = JSON.parse(str).value;
                          } catch (e) {
                            toastService.openToast({
                              ...BASE_TOAST_CONFIG,
                              component: (<div>Error parsing text</div>),
                              type: ELSWithToastService.types.NEGATIVE
                            });
                          }
                          return {
                            ...prevState,
                            convertInput: parsed
                          };
                        });
                      }}
                    >
                      Escaped to String
                    </button>
                  </FlexItem>
                  <FlexItem>
                    <button
                      type="button"
                      className="c-els-button c-els-button--small"
                      onClick={() => {
                        setState((prevState) => {
                          let newValue = state.convertInput;
                          try {
                            const startString = 'XYZ_START_ABC';
                            const stopString = 'XYZ_STOP_ABC';
                            const obj = { value: `${startString}${state.convertInput}${stopString}` };
                            newValue = extractString(JSON.stringify(obj), startString, stopString);
                          } catch (e) {
                            toastService.openToast({
                              ...BASE_TOAST_CONFIG,
                              component: (<div>Error parsing text</div>),
                              type: ELSWithToastService.types.NEGATIVE
                            });
                          }
                          return {
                            ...prevState,
                            convertInput: newValue
                          };
                        });
                      }}
                    >
                      String to Escaped
                    </button>
                  </FlexItem>
                  <FlexItem>
                    <button
                      type="button"
                      className="c-els-button c-els-button--secondary c-els-button--small"
                      onClick={() => {

                        try {
                          const parsed: {
                            promptOverride?: PromptOverrideDto;
                            searchOverride?: SearchOverrideDto;
                            adminOverridePageContext?: string;
                            adminOverride?: {
                              promptOverride?: PromptOverrideDto;
                              searchOverride?: SearchOverrideDto;
                              adminOverridePageContext?: string;
                            };
                          } = JSON.parse(state.convertInput);
                          const overrides = (parsed.adminOverride !== null && typeof parsed.adminOverride === 'object')
                            ? parsed.adminOverride
                            : parsed;
                          setState((prevState) => {
                            return {
                              ...prevState,
                              ...getDefaultOverrideState(
                                overrides.promptOverride,
                                overrides.searchOverride,
                                overrides.adminOverridePageContext || ''
                              ),
                              convertInput: ''
                            };
                          });
                        } catch (e) {
                          toastService.openToast({
                            ...BASE_TOAST_CONFIG,
                            component: (<div>Error parsing text</div>),
                            type: ELSWithToastService.types.NEGATIVE
                          });
                        }
                      }}
                    >
                      Load overrides
                    </button>
                  </FlexItem>
                  <FlexItem>
                    <button
                      type="button"
                      className="c-els-button c-els-button--secondary c-els-button--small"
                      onClick={() => {
                        setState((prevState) => {
                          return {
                            ...prevState,
                            convertInput: ''
                          };
                        });
                      }}
                    >
                      Reset
                    </button>
                  </FlexItem>

                </FlexLayout>
              </div>
            </div>

            <div className="o-els-container o-els-container--2x">
              <h3>Search override</h3>
              <div>
                <div
                  className="c-naa-ai-chat-admin-override-modal__field"
                >
                  <div>Isbns (comma separated: 123,456)</div>
                  <div className="c-els-field">
                    <textarea
                      name="isbns"
                      className="c-els-field__input"
                      value={state.searchOverrideIsbns}
                      onChange={handleIsbnsChange}
                    />
                  </div>
                  <div>
                    Valid list: {getIsbns(state.searchOverrideIsbns).join(',')}
                  </div>
                </div>
              </div>

              <div className="c-naa-ai-chat-admin-override-modal__field">
                <div className="c-els-field">
                  <ELSCheckBox
                    name="includeBaseSet"
                    checked={state.includeBaseSet}
                    changeHandler={() => {
                      setState((prevState) => {
                        return {
                          ...prevState,
                          includeBaseSet: !prevState.includeBaseSet
                        };
                      });
                    }}
                  >
                    Include base set
                  </ELSCheckBox>
                </div>
              </div>

            </div>

            <div className="o-els-container o-els-container--2x">
              <h3>Page context override</h3>
              <div className="c-naa-ai-chat-admin-override-modal__field">
                <div>Admin Override Page Context</div>
                <div className="c-els-field">
                  <textarea
                    name="adminOverridePageContext"
                    className="c-els-field__input"
                    value={state.adminOverridePageContext}
                    onChange={(e) => {
                      const { value } = e.target;
                      setState((prevState) => {
                        return {
                          ...prevState,
                          adminOverridePageContext: value
                        };
                      });
                    }}
                    rows={10}
                    placeholder="Enter page context override (e.g., video transcript, study module content, etc.)"
                  />
                </div>
              </div>
            </div>

            <div className="o-els-container o-els-container--2x">
              <h3>Prompt override</h3>
              <h4 className="o-els-container">Choose the prompts to override</h4>
              {
                Object.keys(state.promptOverride)
                  .map((key) => {
                    return (
                      <div
                        key={key}
                        className="o-els-container o-els-container--1o2"
                      >
                        <ELSCheckBox
                          changeHandler={
                            () => {
                              setState((prevState) => {
                                return {
                                  ...prevState,
                                  promptOverrideActiveKeys: {
                                    ...prevState.promptOverrideActiveKeys,
                                    [key as OverridePromptType]: !prevState.promptOverrideActiveKeys[key as OverridePromptType]
                                  }
                                };
                              });
                            }
                          }
                          checked={state.promptOverrideActiveKeys[key as OverridePromptType]}
                        >
                          {key}
                        </ELSCheckBox>
                      </div>
                    );
                  })
              }
            </div>

            <div>
              {
                Object.keys(state.promptOverride)
                  .filter((key) => {
                    return state.promptOverrideActiveKeys[key];
                  })
                  .map((key) => {
                    return (
                      <div
                        key={key}
                        className="c-naa-ai-chat-admin-override-modal__prompt"
                      >
                        <h4 className="u-els-margin-bottom">{key}</h4>
                        <div className="c-naa-ai-chat-admin-override-modal__description">
                          <div>Expected response format:</div>
                          <div>{responseFormatMap[key as OverridePromptType]}</div>
                        </div>
                        {renderConfig(key as OverridePromptType)}
                      </div>
                    );
                  })
              }
            </div>
          </div>

          <div className="c-naa-ai-chat-admin-override-modal__bottom">
            <FlexLayout modifiers={[
              FlexLayoutModifier.RIGHT,
              FlexLayoutModifier.GUTTERS
            ]}>
              <FlexItem>
                <button
                  type="button"
                  className="c-els-button c-els-button--small c-els-button--secondary"
                  onClick={() => {
                    const _promptOverride = getOverridesForSave();
                    const _searchOverride = getSearchOverridesForSave();
                    handleCopyClick(JSON.stringify({
                      promptOverride: _promptOverride,
                      searchOverride: _searchOverride,
                      adminOverridePageContext: state.adminOverridePageContext
                    }));
                  }}
                >
                  Copy overrides
                </button>
              </FlexItem>
              <FlexItem>
                <button
                  type="button"
                  className="c-els-button c-els-button--small c-els-button--secondary"
                  onClick={() => {
                    const _promptOverride = getOverridesForSave();
                    const _searchOverride = getSearchOverridesForSave();
                    handleCopyClick(JSON.stringify({
                      adminOverride: {
                        promptOverride: _promptOverride,
                        searchOverride: _searchOverride,
                        adminOverridePageContext: state.adminOverridePageContext
                      }
                    }));
                  }}
                >
                  Copy as outpost body
                </button>
              </FlexItem>
              <FlexItem>
                <button
                  type="button"
                  className="c-els-button c-els-button--small"
                  onClick={() => {
                    const _promptOverride = getOverridesForSave();
                    const _searchOverride = getSearchOverridesForSave();
                    handleSave(_promptOverride, _searchOverride, state.adminOverridePageContext);
                  }}
                >
                  Save overrides
                </button>
              </FlexItem>
            </FlexLayout>
          </div>
        </div>
      </div>
    </div>
  );
};

const enhance = flowRight(
  ELSWithToastService,
  withPageLoader,
);

const AiChatAdminModal: React.ComponentType<AiChatAdminModalPropsOnly> =
  enhance(AiChatAdminModalComponent as React.FC<AiChatAdminModalProps>) as React.ComponentType<AiChatAdminModalPropsOnly>;

export default AiChatAdminModal;
